import Button from "@/components/Button";
import ImageUploaderS3 from "@/src/notes/components/ImageUploaderS3";
import useNotes from "@/src/notes/hooks/useNotes";
import { NoteType } from "@/src/notes/types/NoteType";
import { XMarkIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";

const Form = ({
  elementId,
  note = null,
  onEditComplete = () => null,
  onCreateComplete = () => null,
  allowMultipleImages = false,
  parentUid = null,
  isDisabled = false,
}: {
  elementId: number;
  note?: NoteType | null;
  onEditComplete?: (updatedNote: NoteType) => void;
  onCreateComplete?: (newNote: NoteType) => void;
  allowMultipleImages?: boolean;
  parentUid?: string | null;
  isDisabled?: boolean;
}) => {
  const [content, setContent] = useState("");
  const textareaRef = useRef(null);
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [error, setError] = useState("");
  const [noteUid, setNoteUid] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [isInteracting, setIsInteracting] = useState(false);

  const { createNote, updateNote, isCreating, isUpdating } = useNotes({
    elementId,
  });

  const trimmedContent = content.trim();

  useEffect(() => {
    if (note) {
      setContent(note.content);
      setImageUrls(note.imageUrls || []);
      setNoteUid(note.uid);
      setIsEditing(true);
    } else {
      setNoteUid(uuidv4());
      setIsEditing(false);
    }
  }, [note]);

  useEffect(() => {
    adjustTextareaHeight();
  }, [content]);

  const adjustTextareaHeight = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      const newHeight = Math.max(48, textareaRef.current.scrollHeight);
      textareaRef.current.style.height = `${newHeight}px`;
    }
  };

  const handleImagesChange = (urls: string[]) => {
    setImageUrls(urls);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isDisabled) return;

    if (trimmedContent || imageUrls.length > 0) {
      const noteData = {
        uid: noteUid,
        content: trimmedContent,
        imageUrls,
        parentUid,
      };

      if (isEditing) {
        const updatedNoteResponse = await updateNote(noteData);
        if (updatedNoteResponse.success) {
          onEditComplete(updatedNoteResponse.note);
          resetForm();
        } else {
          setError(
            updatedNoteResponse.message ||
              "Failed to update note. Please try again."
          );
        }
      } else {
        const newNoteResponse = await createNote(noteData);
        if (newNoteResponse.success) {
          onCreateComplete(newNoteResponse.note);
          resetForm();
        } else {
          setError(
            newNoteResponse.message ||
              "Failed to create note. Please try again."
          );
        }
      }
    }
  };

  const resetForm = () => {
    setContent("");
    setImageUrls([]);
    setNoteUid(uuidv4());
    setIsEditing(false);
    setError("");
  };

  const handleCancelClick = () => {
    resetForm();
    if (onEditComplete) onEditComplete(note);
  };

  const startInteraction = () => setIsInteracting(true);
  const endInteraction = () => {
    if (!content.trim() && imageUrls.length === 0) {
      setIsInteracting(false);
    }
  };

  const handleTextareaFocus = () => {
    startInteraction();
  };

  const handleTextareaBlur = () => {
    // Only end interaction if there's no content and no files
    if (!content.trim() && imageUrls.length === 0) {
      endInteraction();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && e.shiftKey) {
      e.preventDefault();
      handleSubmit(e as unknown as React.FormEvent<HTMLFormElement>);
    }
  };

  const getButtonText = () => {
    if (isCreating) return "Adding...";
    if (isUpdating) return "Updating...";
    if (isEditing) return "Update note";
    if (parentUid) return "Add reply";
    return "Add note";
  };

  return (
    (<form onSubmit={handleSubmit} className={clsx("")}>
      <fieldset
        className={clsx(
          "relative z-10 flex flex-col flex-wrap overflow-hidden rounded-2xl border bg-white transition ease-out lg:flex-row",
          {
            "border-zinc-300": !isInteracting,
            "border-zinc-950": isInteracting,
          }
        )}
      >
        <div className={clsx("flex w-full flex-1 px-4 py-4 lg:w-5/6 lg:px-4")}>
          <textarea
            ref={textareaRef}
            value={content}
            onChange={(e) => {
              setContent(e.target.value);
              adjustTextareaHeight();
            }}
            onKeyDown={handleKeyDown}
            onFocus={handleTextareaFocus}
            onBlur={handleTextareaBlur}
            placeholder="Tell us what you think..."
            className={clsx(
              "flex-1 resize-none overflow-hidden font-body text-base font-normal placeholder:text-zinc-500 focus:outline-none lg:text-sm",
              {
                "": isCreating || isUpdating,
                "pointer-events-none opacity-50": isDisabled,
              }
            )}
            rows={1}
            disabled={isCreating || isUpdating || isDisabled}
          />
        </div>
        <ImageUploaderS3
          noteUid={noteUid}
          allowMultipleImages={allowMultipleImages}
          isDisabled={isDisabled}
          onImagesChange={handleImagesChange}
          initialImageUrls={imageUrls}
        />
      </fieldset>
      {error && (
        <div className={clsx("mt-2 font-body text-sm text-red-500")}>
          {error}
        </div>
      )}
      <AnimatePresence>
        {(trimmedContent || imageUrls.length > 0) && (
          <motion.div
            className={clsx("flex w-full  items-center ")}
            exit={{ height: 0, opacity: 0 }}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            transition={{ ease: "easeOut" }}
          >
            <div
              className={clsx(
                "flex w-full flex-col items-center lg:flex-row lg:justify-between"
              )}
            >
              <Link
                href="/code-of-conduct"
                className={clsx(
                  "w-full py-4 font-body text-sm text-zinc-500 transition ease-out hover:text-zinc-900 lg:w-3/5"
                )}
                target="_blank">
                
                  Please adhere to the Mob&apos;s code of conduct
                
              </Link>
              <div
                className={clsx(
                  "flex w-full items-center space-x-2 lg:ml-auto lg:w-2/5"
                )}
              >
                <Button
                  className={clsx("")}
                  isDisabled={
                    isCreating ||
                    isUpdating ||
                    isDisabled ||
                    (!content.trim() && imageUrls.length === 0)
                  }
                  onClick={handleCancelClick}
                  isSmall
                  icon={XMarkIcon}
                  isFull
                >
                  Cancel
                </Button>
                <Button
                  isSmall
                  type="submit"
                  theme="light"
                  className={clsx("", {
                    "cursor-not-allowed opacity-50":
                      isCreating ||
                      isUpdating ||
                      isDisabled ||
                      (!content.trim() && imageUrls.length === 0),
                    "":
                      !isCreating &&
                      !isUpdating &&
                      !isDisabled &&
                      (content.trim() || imageUrls.length > 0),
                  })}
                  isDisabled={
                    isCreating ||
                    isUpdating ||
                    isDisabled ||
                    (!content.trim() && imageUrls.length === 0)
                  }
                  isLoading={isCreating || isUpdating}
                  isFull
                >
                  {getButtonText()}
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </form>)
  );
};

export default Form;
